import { Action } from '../actions';
import { BidAwardStatusApi } from '../api-typings/projects/projects';
import { transformBid } from '../bid/bid.transformers';
import {
  mergeEntities,
  mergeListEntities,
  Path,
  pluckListItemFromRawStoreSlice,
  RawStoreListStateSlice,
  removeEntityById,
  transformObjects,
} from '../helpers';

export function threadBidsReducer(
  state: RawStoreListStateSlice<'threadBids'> = {},
  action: Action,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'threadBids') {
        const { result, refList } = action.payload;
        return mergeListEntities<'threadBids'>(
          state,
          transformObjects(result.bids, transformBid),
          refList,
          'projectId',
        );
      }
      return state;
    }
    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'threadBids') {
        const { object: delta, originalObject, ref } = action.payload;
        return mergeEntities<'threadBids'>(
          state,
          transformObjects([originalObject.id.toString()], projectId => {
            const bid = pluckListItemFromRawStoreSlice(
              state,
              ref.path,
              projectId,
            );
            if (bid && delta.awardStatus) {
              const awardStatus: BidAwardStatusApi =
                delta.awardStatus || 'pending';
              return { ...bid, awardStatus };
            }
            return undefined;
          }),
          ref,
        );
      }
      return state;
    }
    case 'WS_MESSAGE': {
      if (action.payload.parent_type === 'notifications') {
        const pathAndRef = (id: number) => {
          const path: Path<'threadBids'> = ['threadBids', String(id)];
          return {
            path,
            ref: { path },
          };
        };
        switch (action.payload.type) {
          case 'bid':
          case 'bid_self':
          case 'bid_updated': {
            const bidObj = {
              id: action.payload.data.bid.id,
              bidderId: action.payload.data.bid.userId,
              projectId: action.payload.data.projectId,
              amount: Number(action.payload.data.amount),
              period: Number(action.payload.data.period),
              description: action.payload.data.bid.descr || '',
            };
            const { path, ref } = pathAndRef(action.payload.data.projectId);
            return mergeEntities<'threadBids'>(
              state,
              transformObjects([String(action.payload.data.projectId)], _ => {
                const threadBid = pluckListItemFromRawStoreSlice(
                  state,
                  path,
                  bidObj.id,
                );
                return {
                  ...threadBid,
                  ...bidObj,
                };
              }),
              ref,
            );
          }

          case 'bidretracted': {
            const { ref } = pathAndRef(action.payload.data.project_id);
            return removeEntityById<'threadBids'>(
              ref,
              state,
              action.payload.data.bid_id,
            );
          }

          case 'revoked': {
            const { bid, project } = action.payload.data.apiMessage;
            const bidObj = {
              id: bid.id,
              bidderId: bid.bidder_id,
              projectId: project.id,
              amount: 0,
              period: 0,
              description: '', // XXX Not trusting the format in the model, so we'll default for now.
              awardStatus: bid.award_status,
            };
            const { path, ref } = pathAndRef(project.id);
            return mergeEntities<'threadBids'>(
              state,
              transformObjects([String(project.id)], _ => {
                const threadBid = pluckListItemFromRawStoreSlice(
                  state,
                  path,
                  bidObj.id,
                );
                return {
                  ...threadBid,
                  ...bidObj,
                };
              }),
              ref,
            );
          }

          case 'accepted': {
            const { bid, project } = action.payload.data.apiMessage;
            const bidObj = {
              id: bid.id,
              bidderId: bid.bidder_id,
              projectId: project.id,
              amount: bid.amount,
              retracted: action.payload.data.bid.retracted,
              period: bid.period,
              description: '', // XXX Not trusting the format in the model, so we'll default for now.
              awardStatus: bid.award_status,
            };
            const { path, ref } = pathAndRef(project.id);
            return mergeEntities<'threadBids'>(
              state,
              transformObjects([String(project.id)], _ => {
                const threadBid = pluckListItemFromRawStoreSlice(
                  state,
                  path,
                  bidObj.id,
                );
                return {
                  ...threadBid,
                  ...bidObj,
                };
              }),
              ref,
            );
          }

          case 'denyed': {
            const { project, bid } = action.payload.data.apiMessage;
            const awardStatus = BidAwardStatusApi.REJECTED;
            const bidObj = {
              id: Number(action.payload.data.bidId),
              bidderId: bid.bidder_id,
              projectId: project.id,
              amount: 0,
              period: 0,
              description: '', // XXX Not trusting the format in the model, so we'll default for now.
              awardStatus,
            };
            const { path, ref } = pathAndRef(project.id);
            return mergeEntities<'threadBids'>(
              state,
              transformObjects([String(project.id)], _ => {
                const threadBid = pluckListItemFromRawStoreSlice(
                  state,
                  path,
                  bidObj.id,
                );
                return {
                  ...threadBid,
                  ...bidObj,
                };
              }),
              ref,
            );
          }

          case 'award': {
            const { bid, project } = action.payload.data.apiMessage;
            const bidObj = {
              id: bid.id,
              bidderId: bid.bidder_id,
              projectId: project.id,
              amount: bid.amount,
              period: bid.period,
              description: '', // XXX Not trusting the format in the model, so we'll default for now.
              awardStatus: bid.award_status,
            };
            const { path, ref } = pathAndRef(project.id);
            return mergeEntities<'threadBids'>(
              state,
              transformObjects([String(project.id)], _ => {
                const threadBid = pluckListItemFromRawStoreSlice(
                  state,
                  path,
                  bidObj.id,
                );
                return {
                  ...threadBid,
                  ...bidObj,
                };
              }),
              ref,
            );
          }

          default:
            return state;
        }
      }
      return state;
    }
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/thread/threadBid.reducer.ts